import { AuthService } from './../../core/auth.service';
import { Injectable } from '@angular/core';
import { doc, Firestore, getDoc } from '@angular/fire/firestore';
import { MatDialog } from '@angular/material/dialog';
import { Data } from '@angular/router';
import * as dayjs from 'dayjs';
import { Timestamp } from "firebase/firestore";
import { of, asyncScheduler } from 'rxjs';
import { CommonVariable } from 'src/app/app.constants';
import { NarratorTipsComponent } from 'src/app/components/narrator-tips/narrator-tips.component';
import { NarratorService } from '../narrator.service';
import { catchError, observeOn } from 'rxjs/operators';

@Injectable({
  providedIn: 'root'
})
export class NarratorTipService {
  selectedLanguage: string = CommonVariable?.preferredLanguage;
  narratorData: Data;
  /** Onboarding already presented these templates — inbox must not replay them. */
  private readonly suppressedTipIds = new Set<string>();

  constructor(
    private firestore: Firestore,
    private narratorService: NarratorService,
    private matDialog: MatDialog,
    private authService: AuthService,
  ) { }

  // This function used to process the narrator tip from url
  async narratorTipEvent(
    tip: string,
    options?: { skipInbox?: boolean; skipCloseAll?: boolean }
  ) {
    if (!tip) {
      return;
    }

    try {
      const modifiedTip = tip.startsWith("tip_") ? tip : `tip_${tip}`;
      const i18nPath = `/Global_lookup/user_messages_templates/narrator_tip/${modifiedTip}/i18n/${modifiedTip}_${this.selectedLanguage}`;
      const defaultPath = `/Global_lookup/user_messages_templates/narrator_tip/${modifiedTip}`;

      const i18nDocRef = doc(this.firestore, i18nPath);
      const defaultDocRef = doc(this.firestore, defaultPath);

      const i18nDocSnapshot = await getDoc(i18nDocRef);
      const selectedDocRef = i18nDocSnapshot.exists() ? i18nDocRef : defaultDocRef;

      const selectedDocSnapshot = await getDoc(selectedDocRef);
      const data = selectedDocSnapshot.data();

      if (!data) {
        console.error(`Tip '${modifiedTip}' not found in both paths`);
        return;
      }

      if (options?.skipInbox) {
        this.suppressInboxReplay(modifiedTip);
      }

      // Games onboarding CF already writes issue_message_bundle — a second inbox
      // create here makes the same tip show again from inbox / NotificationManager.
      if (!options?.skipInbox) {
        const ttl = Timestamp.fromDate(dayjs().add(2, "day").toDate());
        const creationTimestamp = new Date();
        const path_id = "8MeSnBfFkPn93kXGFJ0g";
        const doc_data = {
          messages: [modifiedTip],
          creation_timestamp: creationTimestamp,
          ttl: ttl
        };

        this.authService
          .getV2CloudFunction("updatefirestoredoc", {
            path_id,
            doc_data,
            method: "create",
          })
          .subscribe({
            error: (error) => {
              this.authService.errorHandlerLogs("Error triggering inbox points usage message:", error);
              return false;
            }
          });
      }

      // Store tips are shown via page-level narratorShow + NotificationManager once the user doc exists.
      if (data?.setting?.page_name !== "store") {
        this.narratorTipOpen(data, options?.skipCloseAll === true);
      }
    } catch (error) {
      this.authService.errorHandlerLogs("Error handling narrator tip event:", error);
    }
  }
  suppressInboxReplay(tipId: string): void {
    const id = String(tipId || '').trim();
    if (!id) {
      return;
    }
    this.suppressedTipIds.add(id);
    this.suppressedTipIds.add(id.startsWith('tip_') ? id : `tip_${id}`);
  }

  isInboxReplaySuppressed(tipId: string | null | undefined): boolean {
    if (!tipId) {
      return false;
    }
    const id = String(tipId).trim();
    return (
      this.suppressedTipIds.has(id) ||
      this.suppressedTipIds.has(id.startsWith('tip_') ? id : `tip_${id}`)
    );
  }

  // This function is used to open the narrator tip dialog
  public narratorTipOpen(narratorData: Data, skipCloseAll = false) {
    const pageNameTip = this.narratorService.getCurrentPageURL(); // page name with tip
    const pageName = pageNameTip.includes("?") ? pageNameTip.split("?")[0] : pageNameTip; // extracting page name for check
    if (
      ((narratorData?.setting?.page_name === pageName) || (narratorData?.setting?.page_name === "checkout"))
      && narratorData?.setting?.seen !== true
    ) {
      const alreadyOpen = this.matDialog.openDialogs.some(
        (d) => d.componentInstance instanceof NarratorTipsComponent
      );
      if (alreadyOpen) {
        return;
      }
      this.setNarratorId();
      if (!skipCloseAll) {
        this.matDialog.closeAll();
      }
      this.matDialog.open(NarratorTipsComponent, {
        panelClass: "narrator-tip",
        height: "100%",
        width: "100%",
        data: narratorData,
      });
    }
  }

// This function is used to set the narrator id in local storage
// It fetches the narrator tips and checks if there is a tip for the current page that has not been seen
// Narrrator Tip ID required for updating the narrator tip state to seen = true
public setNarratorId(): void {
  this.narratorService
    ?.getNarratorTipsHome()
    .pipe(
      catchError((error) => {
        this.authService.errorHandlerLogs("Error fetching narrator tips:", error);
        return of([] as Data[]);
      }),
      observeOn(asyncScheduler)
    )
    .subscribe({
      next: (response: Data[]) => {
        this.narratorData = [];

        // Validate runtime type
        if (!Array.isArray(response)) {
          this.authService.errorHandlerLogs("Invalid response format for narrator tips:", response);
          return;
        }

        if (response.length > 0) {
          for (const tip of response as Data[]) {
            try {
              const pageNameTip = this.narratorService?.getCurrentPageURL();
              const pageName = pageNameTip?.includes("?")
                ? pageNameTip.split("?")[0]
                : pageNameTip;
              if (((tip?.setting?.page_name === pageName) || (tip?.setting?.page_name === 'checkout')) && !tip?.setting?.seen  ) {
                this.narratorData = tip;
                localStorage.setItem("narratorID", this.narratorData?.id);
                break;
              }
            } catch (err) {
              this.authService.errorHandlerLogs("Error processing narrator tip:", err);
            }
          }
        }
      },
      error: (err) => {
        // Extra defensive logging
        this.authService.errorHandlerLogs("Subscription error in setNarratorId", err);
      },
    });
}


}
